import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, LayoutGrid, Send, Mail, Plus, Loader2, Rocket, Sparkles,
  Twitter, Linkedin, Instagram, Star, Trash2, CheckCircle2, Users, ArrowLeft,
  LogOut, Share2, AlertCircle, BarChart3, TicketPercent, Settings,
  DollarSign, ShoppingBag, TrendingUp, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const PLATFORM_ICON = { X: Twitter, LinkedIn: Linkedin, Instagram: Instagram };
const TABS = [
  { id: "products", label: "Products", icon: LayoutGrid },
  { id: "sales", label: "Sales", icon: BarChart3 },
  { id: "social", label: "Social Scheduler", icon: Send },
  { id: "email", label: "Email Engine", icon: Mail },
  { id: "promos", label: "Promo Codes", icon: TicketPercent },
  { id: "settings", label: "Settings", icon: Settings },
];

function shareTo(platform, content, hashtags) {
  const text = `${content}\n\n${(hashtags || []).join(" ")}`.trim();
  try {
    navigator.clipboard?.writeText(text);
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.debug("clipboard write skipped", e?.message || e);
  }
  let url;
  if (platform === "X") url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  else if (platform === "LinkedIn") url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
  else url = "https://www.instagram.com/";
  window.open(url, "_blank", "noopener,noreferrer");
}

export default function Admin() {
  const [tab, setTab] = useState("products");
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-slate-900/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Ticker Templates" className="h-8 w-auto" />
            <div className="hidden border-l border-white/10 pl-3 sm:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Admin · Trading Desk
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="admin-back-link"
            className="flex items-center gap-2 border border-white/10 px-3 py-2 text-sm text-slate-300 transition-colors duration-200 hover:border-white/30 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Storefront
          </Link>
          <span className="hidden font-mono text-xs text-slate-500 sm:inline">{user?.email}</span>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 border border-white/10 px-3 py-2 text-sm text-slate-300 transition-colors duration-200 hover:border-red-500/50 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1500px] grid-cols-1 lg:grid-cols-12">
        {/* Sidebar */}
        <nav className="border-b border-white/10 lg:col-span-2 lg:min-h-[calc(100vh-65px)] lg:border-b-0 lg:border-r">
          <div className="flex gap-1 p-3 lg:flex-col">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex flex-1 items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors duration-200 ${
                  tab === t.id
                    ? "bg-emerald-500 font-semibold text-slate-900"
                    : "text-slate-300 hover:bg-white/5"
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
        </nav>

        <main className="p-6 lg:col-span-10 lg:p-10">
          {tab === "products" && <ProductsTab />}
          {tab === "sales" && <SalesTab />}
          {tab === "social" && <SocialTab />}
          {tab === "email" && <EmailTab />}
          {tab === "promos" && <PromosTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

/* ------------------------- Products ------------------------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(
    () => api.products({ include_drafts: true, sort: "popular" }).then(setProducts).finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const publish = async (p) => {
    setBusy(p.id);
    try {
      const r = await api.publishProduct(p.id);
      toast.success("Published + autopilot fired", {
        description: `${r.posts_created} social posts synthesized by AI.`,
      });
      await load();
    } catch {
      toast.error("Publish failed");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (p) => {
    await api.deleteProduct(p.id);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between border-b border-white/10 pb-5">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Catalog</span>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tighter">Products</h1>
        </div>
        <button
          data-testid="new-product-btn"
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400"
        >
          <Plus className="h-4 w-4" /> New Template
        </button>
      </div>

      {showForm && <ProductForm onCreated={() => { setShowForm(false); load(); }} />}

      {loading ? (
        <div className="flex justify-center py-24 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="border border-white/10">
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 bg-slate-950 px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-500 md:grid">
            <div className="col-span-4">Template</div>
            <div className="col-span-2">Software</div>
            <div className="col-span-1">Price</div>
            <div className="col-span-1">Rating</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-row-${p.id}`}
              className="grid grid-cols-2 items-center gap-4 border-b border-white/10 px-5 py-4 text-sm last:border-b-0 md:grid-cols-12"
            >
              <div className="col-span-2 flex items-center gap-3 md:col-span-4">
                <img src={p.image_url} alt="" className="h-10 w-14 flex-shrink-0 object-cover" />
                <span className="font-medium text-white">{p.title}</span>
              </div>
              <div className="col-span-2 text-slate-400 md:col-span-2">{p.software_type}</div>
              <div className="col-span-1 font-mono text-emerald-400">
                {p.base_price === 0 ? "Free" : `$${p.base_price}`}
              </div>
              <div className="col-span-1 flex items-center gap-1 font-mono text-slate-300">
                <Star className="h-3 w-3 fill-emerald-400 text-emerald-400" /> {p.rating}
              </div>
              <div className="col-span-2">
                <span
                  className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                    p.status === "Published"
                      ? "border-emerald-500/40 text-emerald-400"
                      : "border-slate-600 text-slate-400"
                  }`}
                >
                  {p.status}
                </span>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                {p.status !== "Published" && (
                  <button
                    data-testid={`publish-btn-${p.id}`}
                    onClick={() => publish(p)}
                    disabled={busy === p.id}
                    className="flex items-center gap-1.5 bg-emerald-500 px-2.5 py-1.5 font-mono text-[11px] font-bold uppercase text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
                  >
                    {busy === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />}
                    Publish
                  </button>
                )}
                <button
                  data-testid={`delete-btn-${p.id}`}
                  onClick={() => remove(p)}
                  className="flex h-7 w-7 items-center justify-center border border-white/10 text-slate-400 transition-colors duration-200 hover:border-red-500/50 hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductForm({ onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", software_type: "Notion", category: "Business",
    tier: "Pro", base_price: 29, competitor_avg: 59, rating: 4.7, reviews: 120,
    page_count: 20, dimensions: "Responsive", file_size: "2 MB",
    formats: "Notion", features: "", status: "Draft",
    image_url: "https://images.unsplash.com/photo-1642132652860-603f4e3c19b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.createProduct({
        ...form,
        base_price: Number(form.base_price),
        competitor_avg: Number(form.competitor_avg),
        rating: Number(form.rating),
        reviews: Number(form.reviews),
        page_count: Number(form.page_count),
        formats: form.formats.split(",").map((s) => s.trim()).filter(Boolean),
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(
        form.status === "Published" ? "Created & published — autopilot fired" : "Draft created"
      );
      onCreated(r);
    } catch {
      toast.error("Could not create template");
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full border border-white/15 bg-slate-950 px-3 py-2 text-sm text-white outline-none transition-colors duration-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500";

  return (
    <form onSubmit={submit} data-testid="product-form" className="mb-8 border border-white/10 bg-slate-950 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="md:col-span-2">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Title</span>
          <input data-testid="form-title" className={input} required value={form.title} onChange={(e) => set("title", e.target.value)} />
        </label>
        <label className="md:col-span-2">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Description</span>
          <textarea data-testid="form-description" rows={2} className={input} required value={form.description} onChange={(e) => set("description", e.target.value)} />
        </label>
        <label>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Software</span>
          <select className={input} value={form.software_type} onChange={(e) => set("software_type", e.target.value)}>
            {["Notion", "Excel", "Figma", "PowerPoint", "Google Sheets", "Canva"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Tier</span>
          <select className={input} value={form.tier} onChange={(e) => set("tier", e.target.value)}>
            {["Free", "Pro", "Premium"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Our Price ($)</span>
          <input data-testid="form-price" type="number" step="1" className={input} value={form.base_price} onChange={(e) => set("base_price", e.target.value)} />
        </label>
        <label>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Market Avg ($)</span>
          <input type="number" step="1" className={input} value={form.competitor_avg} onChange={(e) => set("competitor_avg", e.target.value)} />
        </label>
        <label>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Rating</span>
          <input type="number" step="0.1" max="5" className={input} value={form.rating} onChange={(e) => set("rating", e.target.value)} />
        </label>
        <label>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Formats (comma sep)</span>
          <input className={input} value={form.formats} onChange={(e) => set("formats", e.target.value)} />
        </label>
        <label className="md:col-span-2">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Features (one per line)</span>
          <textarea data-testid="form-features" rows={3} className={input} placeholder="Integrated CRM&#10;OKR tracking" value={form.features} onChange={(e) => set("features", e.target.value)} />
        </label>
        <label className="md:col-span-2">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Image URL</span>
          <input className={input} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} />
        </label>
      </div>
      <div className="mt-5 flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input data-testid="form-publish-toggle" type="checkbox" checked={form.status === "Published"} onChange={(e) => set("status", e.target.checked ? "Published" : "Draft")} className="accent-emerald-500" />
          Publish immediately (fires autopilot)
        </label>
        <button data-testid="form-submit" disabled={busy} className="ml-auto flex items-center gap-2 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Template
        </button>
      </div>
    </form>
  );
}

/* ------------------------- Social ------------------------- */
function SocialTab() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => api.socialPosts(filter).then(setPosts).finally(() => setLoading(false)), [filter]);
  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const markPosted = async (id) => {
    await api.markPosted(id);
    toast.success("Marked as posted");
    load();
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Autopilot</span>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
            <Sparkles className="h-6 w-6 animate-pulse text-emerald-400" /> Social Scheduler
          </h1>
          <p className="mt-1 text-sm text-slate-400">AI synthesizes posts the moment a product is published.</p>
        </div>
        <div className="flex gap-2">
          {["All", "X", "LinkedIn", "Instagram"].map((p) => (
            <button
              key={p}
              data-testid={`social-filter-${p.toLowerCase()}`}
              onClick={() => setFilter(p)}
              className={`border px-3 py-1.5 text-sm transition-colors duration-200 ${
                filter === p ? "border-emerald-500 text-emerald-300" : "border-white/10 text-slate-300 hover:border-white/30"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="border border-white/10 bg-slate-950 py-24 text-center text-slate-400">
          No posts yet. Publish a product to fire the autopilot engine.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {posts.map((p) => {
            const Icon = PLATFORM_ICON[p.platform] || Twitter;
            return (
              <div key={p.id} data-testid={`social-post-${p.id}`} className="flex flex-col border border-white/10 bg-slate-950 p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center bg-emerald-500/15 text-emerald-400"><Icon className="h-4 w-4" /></div>
                    <span className="font-mono text-xs uppercase tracking-wider text-slate-400">{p.platform}</span>
                  </div>
                  <span className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${p.status === "Posted" ? "border-slate-600 text-slate-400" : "border-emerald-500/40 text-emerald-400"}`}>
                    {p.status}
                  </span>
                </div>
                <p className="mb-3 flex-1 text-sm leading-relaxed text-slate-200">{p.content}</p>
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {p.hashtags?.map((h) => <span key={h} className="font-mono text-[11px] text-emerald-500">{h}</span>)}
                </div>
                {p.status !== "Posted" && (
                  <div className="flex gap-2">
                    <button
                      data-testid={`post-now-${p.id}`}
                      onClick={async () => {
                        shareTo(p.platform, p.content, p.hashtags);
                        await api.markPosted(p.id);
                        toast.success(`Opening ${p.platform}`, { description: "Caption copied to clipboard — paste & post." });
                        load();
                      }}
                      className="flex flex-1 items-center justify-center gap-1.5 bg-emerald-500 py-2 text-xs font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400"
                    >
                      <Share2 className="h-3.5 w-3.5" /> Post now
                    </button>
                    <button
                      data-testid={`mark-posted-${p.id}`}
                      onClick={() => markPosted(p.id)}
                      className="flex items-center justify-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-slate-300 transition-colors duration-200 hover:border-emerald-500 hover:text-emerald-400"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Email ------------------------- */
function EmailTab() {
  const [digest, setDigest] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadDigest = useCallback(() => {
    setLoading(true);
    return api.digest().then(setDigest).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDigest();
    api.subscribers().then(setSubscribers).catch((err) => console.error("Failed to load subscribers", err?.message || err));
  }, [loadDigest]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.sendDigest();
      if (r.mode === "sent") toast.success("Digest emailed", { description: `Sent to ${r.recipients} subscribers via Resend.` });
      else if (r.mode === "simulated") toast.info("Send simulated", { description: r.message });
      else toast.error(r.message || "Nothing sent");
      loadDigest();
    } catch {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Autopilot</span>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tighter">Retention Email Engine</h1>
          <p className="mt-1 text-sm text-slate-400">Weekly digest auto-curated from the highest-rated templates.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="regen-digest-btn"
            onClick={loadDigest}
            className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-slate-300 transition-colors duration-200 hover:border-emerald-500 hover:text-emerald-400"
          >
            <Sparkles className="h-4 w-4" /> Regenerate
          </button>
          <button
            data-testid="send-digest-btn"
            onClick={sendNow}
            disabled={sending}
            className="flex items-center gap-2 bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send digest now
          </button>
        </div>
      </div>

      {digest && !digest.email_configured && (
        <div data-testid="email-not-configured" className="mb-6 flex items-start gap-3 border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>Email provider not connected. "Send digest now" runs in <strong>simulated</strong> mode. Add a RESEND_API_KEY to send real emails and auto-send weekly.</span>
        </div>
      )}
      {digest?.last_sent && (
        <p className="mb-4 font-mono text-xs text-slate-500">Last sent: {new Date(digest.last_sent).toLocaleString()}</p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Digest preview */}
        <div className="lg:col-span-2">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">Live Preview</p>
          {loading ? (
            <div className="flex justify-center border border-white/10 bg-slate-950 py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : digest ? (
            <div data-testid="digest-preview" className="overflow-hidden border border-white/10 bg-white text-slate-900">
              <div className="border-b border-slate-200 bg-slate-900 px-6 py-4">
                <p className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">Ticker Templates Weekly · to {digest.recipients} subscribers</p>
                <h3 className="mt-1 font-display text-xl font-extrabold tracking-tight text-white">{digest.subject}</h3>
              </div>
              <div className="p-6">
                <p className="text-sm leading-relaxed text-slate-700">{digest.intro}</p>
                <div className="mt-5 space-y-3">
                  {digest.picks?.map((p, i) => (
                    <div key={p.id} className="flex items-center gap-4 border border-slate-200 p-3">
                      <span className="font-display text-2xl font-extrabold text-slate-300">{i + 1}</span>
                      <img src={p.image_url} alt="" className="h-12 w-16 object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-900">{p.title}</p>
                        <p className="flex items-center gap-1 text-xs text-slate-500">
                          <Star className="h-3 w-3 fill-emerald-500 text-emerald-500" /> {p.rating} · {p.software_type}
                        </p>
                      </div>
                      <span className="font-mono text-sm font-bold text-emerald-600">
                        {p.base_price === 0 ? "Free" : `$${p.base_price}`}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 bg-emerald-500 py-3 text-center font-semibold text-slate-900">Browse the full catalog →</div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Subscribers */}
        <div>
          <p className="mb-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">
            <Users className="h-3.5 w-3.5" /> Subscribers ({subscribers.length})
          </p>
          <div className="border border-white/10 bg-slate-950">
            {subscribers.length === 0 ? (
              <p className="p-6 text-sm text-slate-400">No subscribers yet. The capture blocks feed this list.</p>
            ) : (
              subscribers.slice(0, 12).map((s) => (
                <div key={s.id} data-testid={`subscriber-${s.id}`} className="flex items-center justify-between border-b border-white/10 px-4 py-3 text-sm last:border-b-0">
                  <span className="truncate text-slate-200">{s.email}</span>
                  <span className="ml-2 flex-shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.source}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Sales ------------------------- */
function SalesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.sales().then(setData).catch((err) => console.error("Failed to load sales", err?.message || err)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!data) return null;

  const stats = [
    { label: "Total Revenue", value: `$${data.total_revenue.toLocaleString()}`, icon: DollarSign },
    { label: "Orders", value: data.total_orders, icon: ShoppingBag },
    { label: "Avg Order Value", value: `$${data.aov}`, icon: TrendingUp },
  ];

  return (
    <div>
      <div className="mb-8 border-b border-white/10 pb-5">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Overview</span>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tighter">Sales Dashboard</h1>
        <p className="mt-1 text-sm text-slate-400">Revenue, orders and your best-selling templates.</p>
      </div>

      <div className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
        {stats.map((s) => (
          <div key={s.label} data-testid={`sales-stat-${s.label.replace(/\s/g, "-").toLowerCase()}`} className="bg-slate-950 p-6">
            <s.icon className="mb-3 h-5 w-5 text-emerald-400" />
            <div className="font-mono text-3xl font-bold text-white">{s.value}</div>
            <div className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 border border-white/10 bg-slate-950 p-6">
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">Revenue · last 30 days</p>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <AreaChart data={data.revenue_series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={5} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} formatter={(v) => [`$${v}`, "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#34d399" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-white/10 bg-slate-950 p-6">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">Top Sellers</p>
          <div className="space-y-3">
            {data.top_products.map((p, i) => (
              <div key={p.product_id} data-testid={`top-product-${i}`} className="flex items-center gap-3">
                <span className="font-display text-lg font-extrabold text-slate-600">{i + 1}</span>
                <span className="flex-1 truncate text-sm text-slate-200">{p.title}</span>
                <span className="font-mono text-xs text-slate-500">{p.units}×</span>
                <span className="font-mono text-sm font-bold text-emerald-400">${p.revenue}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-slate-950 p-6">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">Recent Orders</p>
          <div className="space-y-3">
            {data.recent_orders.map((o, i) => (
              <div key={`${o.date}-${o.buyer_email}-${i}`} data-testid={`recent-order-${i}`} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm last:border-b-0">
                <div className="min-w-0">
                  <p className="truncate text-slate-200">{o.title}</p>
                  <p className="truncate font-mono text-[11px] text-slate-500">{o.buyer_email}</p>
                </div>
                <span className="ml-3 flex-shrink-0 font-mono text-sm font-bold text-emerald-400">${o.amount}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Promo Codes ------------------------- */
function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(10);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.promos().then(setPromos).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createPromo(code.toUpperCase(), Number(percent));
      toast.success("Promo code created", { description: `${code.toUpperCase()} works at checkout now.` });
      setCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create code");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    await api.deletePromo(p.id);
    toast.success("Code deactivated");
    load();
  };

  const input = "w-full border border-white/15 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none transition-colors duration-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500";

  return (
    <div>
      <div className="mb-8 border-b border-white/10 pb-5">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Discounts</span>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tighter">Promo Codes</h1>
        <p className="mt-1 text-sm text-slate-400">Create codes buyers redeem on the Stripe checkout page.</p>
      </div>

      <form onSubmit={create} data-testid="promo-form" className="mb-8 flex flex-col gap-3 border border-white/10 bg-slate-950 p-6 sm:flex-row sm:items-end">
        <label className="flex-1">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Code</span>
          <input data-testid="promo-code-input" className={input} required placeholder="LAUNCH20" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </label>
        <label className="w-full sm:w-40">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">% Off</span>
          <input data-testid="promo-percent-input" type="number" min="1" max="100" className={input} value={percent} onChange={(e) => setPercent(e.target.value)} />
        </label>
        <button data-testid="promo-create-btn" disabled={busy} className="flex items-center justify-center gap-2 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : promos.length === 0 ? (
        <div className="border border-white/10 bg-slate-950 py-16 text-center text-slate-400">No active promo codes yet.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {promos.map((p) => (
            <div key={p.id} data-testid={`promo-${p.code}`} className="flex items-center justify-between border border-dashed border-emerald-500/40 bg-emerald-500/5 p-5">
              <div>
                <div className="font-display text-xl font-extrabold tracking-tight text-white">{p.code}</div>
                <div className="font-mono text-xs text-emerald-400">{p.percent_off}% off · applies at checkout</div>
              </div>
              <button data-testid={`promo-delete-${p.code}`} onClick={() => remove(p)} className="flex h-8 w-8 items-center justify-center border border-white/10 text-slate-400 transition-colors duration-200 hover:border-red-500/50 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Settings ------------------------- */
function SettingsTab() {
  const { user, updateSession } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current) {
      toast.error("Enter your current password to confirm changes");
      return;
    }
    setBusy(true);
    try {
      const body = { current_password: current };
      if (email && email !== user?.email) body.email = email;
      if (next) body.new_password = next;
      const r = await api.updateCredentials(body);
      if (r.token) updateSession(r.token, r.user);
      toast.success("Credentials updated", { description: "Your new login is active." });
      setCurrent("");
      setNext("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full border border-white/15 bg-slate-950 px-4 py-3 text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500";

  return (
    <div>
      <div className="mb-8 border-b border-white/10 pb-5">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Account</span>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tighter">Settings</h1>
        <p className="mt-1 text-sm text-slate-400">Update the email and password you use to sign in.</p>
      </div>

      <form onSubmit={submit} data-testid="settings-form" className="max-w-lg space-y-5 border border-white/10 bg-slate-950 p-8">
        <div>
          <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Admin Email</label>
          <input data-testid="settings-email" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">New Password <span className="text-slate-600">(leave blank to keep)</span></label>
          <input data-testid="settings-new-password" type="password" className={input} placeholder="••••••••" value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div className="border-t border-white/10 pt-5">
          <label className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
            <KeyRound className="h-3 w-3" /> Current Password (required to confirm)
          </label>
          <input data-testid="settings-current-password" type="password" required className={input} placeholder="••••••••" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <button data-testid="settings-save-btn" disabled={busy} className="flex items-center gap-2 bg-emerald-500 px-6 py-3 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save changes
        </button>
      </form>
    </div>
  );
}

